import { Button } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { isRouteErrorResponse, useRouteError } from "react-router-dom"
import { isChunkLoadError, reloadForNewVersion } from "@/lib/chunk-reload"
import { errorReference } from "@/lib/sdk"
import { reportClientError } from "@/lib/observability"

/**
 * Shown instead of React Router's developer screen when a page fails to load or crashes. A page
 * that failed because the admin (Keel Admin) was just updated reloads itself into the new version.
 */
export function RouteError() {
  const error = useRouteError()
  const stale = isChunkLoadError(error)
  // null: not tried yet; true: reloading now; false: already reloaded once in the last 30 s.
  const [reloading, setReloading] = useState<boolean | null>(null)
  useEffect(() => {
    if (stale) setReloading(reloadForNewVersion())
  }, [stale])
  // A crash in the page itself goes to System status → Recent errors (lib/observability.ts).
  useEffect(() => {
    if (!stale && !isRouteErrorResponse(error)) reportClientError(error, "page")
  }, [error, stale])
  const reference = stale ? "" : errorReference(error).replace(/^ Reference: /, "")

  const detail = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : String((error as { message?: unknown })?.message ?? "")
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md rounded-xl border border-ui-border-base bg-ui-bg-base p-6 text-center shadow-sm">
        <h1 className="k-h3">{stale ? "Keel Admin was just updated" : "This page couldn't load"}</h1>
        <p className="mt-2 text-[13px] text-ui-fg-subtle">
          {stale ? (reloading === false ? "Reload the page to get the latest version. If this keeps showing, check your connection." : "Loading the new version…") : "Reload to try again. If it keeps happening, send this message to support."}
        </p>
        {!stale && detail ? <p className="mt-3 break-words rounded bg-ui-bg-subtle px-3 py-2 font-mono text-[11px] text-ui-fg-muted">{detail.slice(0, 300)}</p> : null}
        {reference ? <p className="mt-2 font-mono text-[11px] text-ui-fg-muted">Reference: {reference}</p> : null}
        <div className="mt-4 flex justify-center gap-2">
          <Button size="small" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button size="small" variant="secondary" onClick={() => window.location.assign("/")}>
            Go to Home
          </Button>
        </div>
      </div>
    </div>
  )
}
